import React, { createContext, useContext, useState, useEffect } from 'react';
import { Theme } from '../types';

const lightTheme: Theme = {
  colors: {
    primary: '#973031',
    secondary: '#4A4A4A',
    background: '#FFFFFF',
    surface: '#F8F9FA',
    text: '#2D2D2D',
    textSecondary: '#6C757D',
    border: '#E9ECEF',
    error: '#DC3545',
    success: '#28A745',
  },
  isDark: false,
};

const darkTheme: Theme = {
  colors: {
    primary: '#973031',
    secondary: '#B0B0B0',
    background: '#1A1A1A',
    surface: '#2D2D2D',
    text: '#FFFFFF',
    textSecondary: '#B0B0B0',
    border: '#404040',
    error: '#FF6B6B',
    success: '#51CF66',
  },
  isDark: true,
};

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDark(savedTheme === 'dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const theme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
